<template>
  <div :class="['ui-input-group', sizeClass]">
    <!-- Slot wins; falls back to a gray addon span when only the
         `prepend` string prop is set. Slot content renders bare so
         a passed-in <UiButton> doesn't get a gray wrapper around it. -->
    <slot name="prepend">
      <span
        v-if="prepend"
        class="ui-input-group__addon ui-input-group__addon--prepend"
      >{{ prepend }}</span>
    </slot>
    <slot />
    <slot name="append">
      <span
        v-if="append"
        class="ui-input-group__addon ui-input-group__addon--append"
      >{{ append }}</span>
    </slot>
  </div>
</template>

<script setup lang="ts">
  /**
   * UiInputGroup — Tailwind primitive replacing BootstrapVue-Next's
   * `b-input-group`.
   *
   * Wraps an input + adjacent controls (buttons, addons) into a flush
   * horizontal group. Inner borders collapse so adjacent elements share
   * a single divider, and only the outermost corners stay rounded —
   * the same visual the legacy BVN component produced.
   *
   * Three integration patterns are supported, mirroring how the rest
   * of the app already uses input groups:
   *
   * 1. String shorthand (BVN parity) — pass the addon text directly:
   *      <UiInputGroup prepend="$">
   *        <UiInput v-model="amount" type="number" />
   *      </UiInputGroup>
   *
   * 2. Slot — drop arbitrary content (icons, buttons) into prepend/append:
   *      <UiInputGroup>
   *        <UiInput v-model="search" />
   *        <template #append>
   *          <UiButton variant="success" @click="add">Add</UiButton>
   *        </template>
   *      </UiInputGroup>
   *
   * 3. Default-slot composition — multiple controls placed inline:
   *      <UiInputGroup>
   *        <UiInput v-model="name" />
   *        <UiButton variant="danger" @click="cancel"><i class="fa fa-times" /></UiButton>
   *        <UiButton variant="success" @click="save"><i class="fa fa-check" /></UiButton>
   *      </UiInputGroup>
   *
   * The component owns layout only; inner controls keep their own
   * v-model / disabled / focus behaviour. CSS uses `:deep()` to flatten
   * the borders of foreign children (UiInput, b-form-input, UiButton)
   * so the group looks consistent regardless of which control mix the
   * caller drops in.
   */
  import { computed } from 'vue'

  defineOptions({ name: 'UiInputGroup' })

  interface Props {
    /** String addon shown before the default slot (BVN parity). */
    prepend?: string
    /** String addon shown after the default slot (BVN parity). */
    append?: string
    size?: 'sm' | 'md' | 'lg'
  }

  const props = withDefaults(defineProps<Props>(), {
    prepend: '',
    append: '',
    size: 'md',
  })

  const sizeClass = computed(() => `ui-input-group--${props.size}`)
</script>

<style scoped>
  /* `display: flex` to align children, but no forced width — sizes to
     content by default so the group can sit inline next to a button
     group in a toolbar. Callers needing full width (table cells, full-
     row forms) add their own `tw-w-full` / `tw-flex-1` utility. */
  .ui-input-group {
    display: flex;
    align-items: stretch;
  }

  /* Inner input wrappers should grow to fill the row; the input itself
     keeps its own width: 100% so v-model semantics are unaffected. */
  .ui-input-group :deep(.ui-input),
  .ui-input-group :deep(.form-control) {
    flex: 1 1 auto;
    min-width: 0;
  }

  /* Flatten all inner border-bearing elements by default — only the
     outer corners (set below) stay rounded. */
  .ui-input-group :deep(.ui-input__field),
  .ui-input-group :deep(.form-control),
  .ui-input-group :deep(.btn),
  .ui-input-group__addon {
    border-radius: 0;
  }

  /* Leftmost child gets the left radius back. */
  .ui-input-group > *:first-child :deep(.ui-input__field),
  .ui-input-group > *:first-child :deep(.form-control),
  .ui-input-group > *:first-child :deep(.btn),
  .ui-input-group > .ui-input-group__addon:first-child {
    border-top-left-radius: 0.375rem;
    border-bottom-left-radius: 0.375rem;
  }

  /* Rightmost child gets the right radius back. */
  .ui-input-group > *:last-child :deep(.ui-input__field),
  .ui-input-group > *:last-child :deep(.form-control),
  .ui-input-group > *:last-child :deep(.btn),
  .ui-input-group > .ui-input-group__addon:last-child {
    border-top-right-radius: 0.375rem;
    border-bottom-right-radius: 0.375rem;
  }

  /* Overlap adjacent borders by 1px so we render a single divider
     between siblings rather than a doubled-up gray line. */
  .ui-input-group > * + * :deep(.ui-input__field),
  .ui-input-group > * + * :deep(.form-control),
  .ui-input-group > * + * :deep(.btn),
  .ui-input-group > .ui-input-group__addon + .ui-input-group__addon {
    margin-left: -1px;
  }

  /* Lift the focused control above its neighbours so its focus ring
     isn't clipped by the next sibling's border. */
  .ui-input-group :deep(.ui-input__field:focus),
  .ui-input-group :deep(.form-control:focus),
  .ui-input-group :deep(.btn:focus) {
    position: relative;
    z-index: 2;
  }

  /* Addon styling — BVN's input-group-text equivalent. Gray-100 fill,
     gray-300 border to match the input, gray-500 text. */
  .ui-input-group__addon {
    display: inline-flex;
    align-items: center;
    padding: 0 0.75rem;
    background-color: #f3f4f6;          /* gray-100 */
    border: 1px solid #d1d5db;          /* gray-300 */
    color: #6b7280;                     /* gray-500 */
    font-size: 0.875rem;
    white-space: nowrap;
  }

  /* Size tweaks — match UiInput's height tiers so the addon aligns. */
  .ui-input-group--sm .ui-input-group__addon {
    padding: 0 0.625rem;
    font-size: 0.8125rem;
  }
  .ui-input-group--lg .ui-input-group__addon {
    padding: 0 1rem;
    font-size: 1rem;
  }
</style>
